import { Injectable, Signal, inject } from "@angular/core";
import { rxResource } from "@angular/core/rxjs-interop";
import { map, Observable, of } from "rxjs";
import { ApiResponse, DataListItem } from "@core/types";
import { DataListService } from "@core/services";
import { ReportStore } from "./report.store";

@Injectable({ providedIn: "root" })
export class DataListStore {
  private readonly service = inject(DataListService);
  private readonly reportStore = inject(ReportStore);

  private readonly toDataList = <T>(request$: Observable<ApiResponse<T[]>>): Observable<T[]> => request$.pipe(map((res) => res.data ?? []));
  private readonly validId = (id: number | null | undefined): number | undefined => (id && id > 0 ? id : undefined);

  //===========================================
  // Parameterless Resources
  //===========================================
  getReportGroups() {
    return rxResource({
      stream: () => this.toDataList(this.service.getReportGroups()),
      defaultValue: [],
    });
  }

  getCausesOfDeath() {
    return rxResource({
      stream: () => this.toDataList(this.service.getCauseOfDeathList()),
      defaultValue: [],
    });
  }

  getMonths() {
    return rxResource({
      stream: () => this.toDataList(this.service.getMonthList()),
      defaultValue: [],
    });
  }

  getYears() {
    return rxResource({
      stream: () => this.toDataList(this.service.getYearList()),
      defaultValue: [],
    });
  }

  getOrganizationTypes() {
    return rxResource({
      stream: () => this.toDataList(this.service.getOrganizationTypeList()),
      defaultValue: [],
    });
  }

  getScheduleOrganizations() {
    return rxResource({
      stream: () => this.toDataList(this.service.getScheduleOrganizationList()),
      defaultValue: [],
    });
  }

  getScreeningCriteriaOrganizations() {
    return rxResource({
      stream: () => this.toDataList(this.service.getScreeningCriteriaOrganizationList()),
      defaultValue: [],
    });
  }

  getStates() {
    return rxResource({
      stream: () => this.toDataList(this.service.getStateList()),
      defaultValue: [],
    });
  }

  getInvoiceDates() {
    return rxResource({
      stream: () => this.toDataList(this.service.getInvoiceDateList()),
      defaultValue: [],
    });
  }

  getGenderOptions() {
    return rxResource({
      stream: () =>
        of([
          { value: "M", label: "Male" },
          { value: "F", label: "Female" },
        ]) as Observable<DataListItem[]>,
      defaultValue: [],
    });
  }

  //===========================================
  // Parameterized Resources
  //===========================================

  getReportDateTypes(reportId: Signal<number>) {
    return rxResource({
      params: () => this.validId(reportId()),
      stream: ({ params: reportId }) => this.toDataList(this.service.getDateTypes(reportId)),
      defaultValue: [],
    });
  }

  getCoordinators(reportGroupId: Signal<number>) {
    return rxResource({
      params: () => this.validId(reportGroupId()),
      stream: ({ params: reportGroupId }) => this.toDataList(this.service.getCoordinators(reportGroupId)),
      defaultValue: [],
    });
  }

  getSortTypeOptions(reportId: Signal<number>) {
    return rxResource({
      params: () => this.validId(reportId()),
      stream: ({ params: reportId }) => this.toDataList(this.service.getSortTypeOptions(reportId)),
      defaultValue: [],
    });
  }

  getPendingReferralTypes(reportGroupId: Signal<number>) {
    return rxResource({
      params: () => this.validId(reportGroupId()),
      stream: ({ params: reportGroupId }) => this.toDataList(this.service.getPendingReferralTypes(reportGroupId)),
      defaultValue: [],
    });
  }

  getStatTracUsers(reportGroupId: Signal<number | null>) {
    return rxResource({
      params: () => reportGroupId(),
      stream: ({ params: reportGroupId }) => this.toDataList(this.service.getStattracUserList(reportGroupId)),
      defaultValue: [],
    });
  }

  getScreeningCriteriaGroups(organizationId: Signal<number>) {
    return rxResource({
      params: () => this.validId(organizationId()),
      stream: ({ params: organizationId }) => this.toDataList(this.service.getScreeningCriteriaGroupList(organizationId!)),
      defaultValue: [],
    });
  }

  getScheduleGroups(organizationId: Signal<number>) {
    return rxResource({
      params: () => this.validId(organizationId()),
      stream: ({ params: organizationId }) => this.toDataList(this.service.getScheduleGroupList(organizationId!)),
      defaultValue: [],
    });
  }

  getOrganizationByStateAndType(stateId: Signal<number | null>, organizationTypeId: Signal<number | null>) {
    return rxResource({
      params: () => ({
        stateId: stateId(),
        organizationTypeId: organizationTypeId(),
      }),
      stream: ({ params: { stateId, organizationTypeId } }) => {
        if (stateId == null && organizationTypeId == null) return of([]);
        return this.toDataList(this.service.getOrganizationByStateAndTypeList(stateId, organizationTypeId));
      },
      defaultValue: [],
    });
  }

  getOrganizations(reportGroupId: Signal<number>, lastRowId: Signal<number>, term: Signal<string>) {
    return rxResource({
      params: () => ({
        lastRowId: lastRowId(),
        term: term(),
        reportGroupId: reportGroupId(),
      }),
      stream: ({ params: { lastRowId, term, reportGroupId } }) => {
        if (reportGroupId == null || reportGroupId == 0 || (term.length > 0 && term.length < 3)) {
          return of([]);
        }
        return this.toDataList(this.service.getOrganizations(reportGroupId, lastRowId, term));
      },
      defaultValue: [],
    });
  }

  getSourceCodes(reportGroupId: Signal<number>) {
    return rxResource({
      params: () => this.validId(reportGroupId()),
      stream: ({ params: reportGroupId }) => this.toDataList(this.service.getSourceCodes(reportGroupId)),
      defaultValue: [],
    });
  }

  getReferralTypes(reportGroupId: Signal<number>, sourceCodeId: Signal<number | null>) {
    return rxResource({
      params: () => ({
        sourceCodeId: sourceCodeId ? sourceCodeId() : null,
        reportGroupId: reportGroupId(),
      }),
      stream: ({ params: { reportGroupId, sourceCodeId } }) => this.toDataList(this.service.getReferralTypes(reportGroupId, sourceCodeId)),
      defaultValue: [],
    });
  }

  getQATrackingTypes(organizationId: Signal<number>) {
    return rxResource({
      params: () => this.validId(organizationId()),
      stream: ({ params: organizationId }) => this.toDataList(this.service.getQATrackingTypeList(organizationId)),
      defaultValue: [],
    });
  }

  getAlertGroupList(alertTypeId: Signal<number | null>, sourceCodeName: Signal<string | null>) {
    return rxResource({
      params: () => ({
        alertTypeId: alertTypeId ? alertTypeId() : null,
        sourceCodeName: sourceCodeName ? sourceCodeName() : null,
      }),
      stream: ({ params: { alertTypeId, sourceCodeName } }) => this.toDataList(this.service.getAlertGroupList(alertTypeId, sourceCodeName)),
      defaultValue: [],
    });
  }

  getMessageImportOrganizations(sourceCodeName: Signal<string | null>, sourceCodeTypeId: Signal<number | null>, organizationId: Signal<number | null>) {
    return rxResource({
      params: () => ({
        sourceCodeName: sourceCodeName ? sourceCodeName() : null,
        sourceCodeTypeId: sourceCodeTypeId ? sourceCodeTypeId() : null,
        organizationId: organizationId ? organizationId() : null,
      }),
      stream: ({ params }) => this.toDataList(this.service.getMessageImportOrganizationsList(params.sourceCodeName, params.sourceCodeTypeId, params.organizationId)),
      defaultValue: [],
    });
  }

  getOrganizationPeoples(organizationId: Signal<number>, inactive: Signal<number>) {
    return rxResource({
      params: () => ({
        organizationId: organizationId(),
        inactive: inactive(),
      }),
      stream: ({ params }) => this.toDataList(this.service.getPeopleByOrganizationList(params.organizationId, params.inactive)),
      defaultValue: [],
    });
  }

  getApproacherPersons(organizationId: Signal<number>) {
    return rxResource({
      params: () => this.validId(organizationId()),
      stream: ({ params: organizationId }) => this.toDataList(this.service.getApproacherPersonList(organizationId)),
      defaultValue: [],
    });
  }

  getEventMainCategories(state: Signal<string>) {
    return rxResource({
      params: () => state(),
      stream: ({ params: state }) => this.toDataList(this.service.getEventMainCategoryList(state)),
      defaultValue: [],
    });
  }

  getEventSubCategories(eventCategoryId: Signal<number>, state: Signal<string>) {
    return rxResource({
      params: () => ({
        eventCategoryId: eventCategoryId(),
        state: state(),
      }),
      stream: ({ params }) => this.toDataList(this.service.getEventSubCategoryList(params.eventCategoryId, params.state)),
      defaultValue: [],
    });
  }

  getRegistryOwnerStates(displayAllStates: Signal<boolean>) {
    return rxResource({
      params: () => displayAllStates(),
      stream: ({ params: displayAllStates }) => this.toDataList(this.service.getRegistryOwnerStates(displayAllStates)),
      defaultValue: [],
    });
  }

  getSchedulePeoples(organizationId: Signal<number>,scheduleGroupId: Signal<number>) {
    return rxResource({
      params: () => {
        const orgId = this.validId(organizationId());
        const groupId = this.validId(scheduleGroupId());

        //just to avoid invalid APi calls
        if (!orgId || !groupId) return undefined;

        return { orgId, groupId };
      },

      stream: ({ params }) =>
        this.toDataList(this.service.getSchedulePeople(params.orgId, params.groupId)).pipe(map((list) => [{ value: -1, label: "" }, ...list])),
      defaultValue: [],
    });
  }
}
